import './NmSegmentControl.css';
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

export interface SegmentOption<T extends string | number> {
  value: T;
  label: string;
}

interface NmSegmentControlProps<T extends string | number> {
  options: SegmentOption<T>[];
  value: T;
  onChange: (value: T) => void;
  variant?: 'track' | 'pills';
  accent?: boolean;
  className?: string;
  ariaLabel?: string;
  fullWidth?: boolean;
}

type ThumbMetrics = { left: number; width: number; ready: boolean };

/** Segmented control — track variant uses a sliding thumb for switch animation. */
export function NmSegmentControl<T extends string | number>({
  options,
  value,
  onChange,
  variant = 'track',
  accent = false,
  className = '',
  ariaLabel,
  fullWidth = false,
}: NmSegmentControlProps<T>) {
  const isTrack = variant === 'track';
  const [keyboard, setKeyboard] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);
  const btnRefs = useRef<Map<string, HTMLButtonElement>>(new Map());
  const [thumb, setThumb] = useState<ThumbMetrics>({ left: 0, width: 0, ready: false });

  const measureThumb = useCallback(() => {
    if (!isTrack) return;
    const track = trackRef.current;
    const btn = btnRefs.current.get(String(value));
    if (!track || !btn) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    setThumb({
      left: btnRect.left - trackRect.left - track.clientLeft,
      width: btnRect.width,
      ready: true,
    });
  }, [isTrack, value]);

  useLayoutEffect(() => {
    measureThumb();
  }, [measureThumb, options]);

  useEffect(() => {
    if (!isTrack) return;
    const track = trackRef.current;
    if (!track) return;

    const onResize = () => measureThumb();
    window.addEventListener('resize', onResize);

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(onResize);
      observer.observe(track);
    }

    return () => {
      window.removeEventListener('resize', onResize);
      observer?.disconnect();
    };
  }, [isTrack, measureThumb]);

  const setBtnRef = (key: string, el: HTMLButtonElement | null) => {
    if (el) btnRefs.current.set(key, el);
    else btnRefs.current.delete(key);
  };

  const wrapClass = isTrack ? 'nm-segment--track' : 'nm-segment';

  return (
    <div className={`nm-segment ${isTrack ? "nm-segment--animated" : ""} ${fullWidth ? "nm-segment--full" : ""} ${className}`.trim()} data-keyboard={keyboard || undefined} onPointerDown={() => setKeyboard(false)}>
      <div
        ref={isTrack ? trackRef : undefined}
        className={wrapClass}
        role="tablist"
        aria-label={ariaLabel}
      >
        {isTrack ? (
          <div className={`nm-segment__active-copy${accent ? ' nm-segment__active-copy--accent' : ''}`} aria-hidden="true"
            style={{ opacity: thumb.ready ? 1 : 0, clipPath: `inset(3px calc(100% - ${thumb.left + thumb.width}px) 3px ${thumb.left}px round 6px)` }}>
            {options.map(opt => <span className="nm-segment-btn" key={String(opt.value)}>{opt.label}</span>)}
          </div>
        ) : null}
        {options.map((opt) => {
          const key = String(opt.value);
          const active = opt.value === value;
          return (
            <button
              key={key}
              ref={(el) => setBtnRef(key, el)}
              type="button"
              role="tab"
              aria-selected={active}
              tabIndex={active ? 0 : -1}
              onKeyDown={event => {
                setKeyboard(true);
                const direction = window.getComputedStyle(event.currentTarget).direction;
                const index = options.findIndex(item => item.value === value);
                let next = index;
                if (event.key === "Home") next = 0;
                else if (event.key === "End") next = options.length - 1;
                else if (event.key === "ArrowRight") next = (index + (direction === "rtl" ? -1 : 1) + options.length) % options.length;
                else if (event.key === "ArrowLeft") next = (index + (direction === "rtl" ? 1 : -1) + options.length) % options.length;
                else return;
                event.preventDefault();
                onChange(options[next].value);
                btnRefs.current.get(String(options[next].value))?.focus();
              }}
              className={`nm-segment-btn${active ? ' nm-segment-btn--active' : ''}${active && accent ? ' nm-segment-btn--accent' : ''}`}
              onClick={() => onChange(opt.value)}
            >
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
